// src/components/condos/MyCondosPage.tsx
// Shows the current user's submitted condos with status badges.

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BackgroundShapes from '../common/BackgroundShapes';
import { condoService } from '../../services/condoService';
import { Condo } from '../../types/condo';

type CondoStatus = 'approved' | 'pending' | 'declined' | 'draft';

function getStatusBadge(status: CondoStatus | undefined, rejectionReason?: string) {
  switch (status) {
    case 'approved':
      return (
        <span className="inline-block bg-green-100 border border-green-400 text-green-800 text-xs font-semibold px-2 py-0.5 rounded">
          Published
        </span>
      );
    case 'pending':
      return (
        <span className="inline-block bg-yellow-100 border border-yellow-400 text-yellow-800 text-xs font-semibold px-2 py-0.5 rounded">
          Under Review
        </span>
      );
    case 'declined':
      return (
        <span className="inline-block bg-red-100 border border-red-400 text-red-800 text-xs font-semibold px-2 py-0.5 rounded">
          Returned for Revision
        </span>
      );
    case 'draft':
      return (
        <span className="inline-block bg-gray-100 border border-gray-400 text-gray-700 text-xs font-semibold px-2 py-0.5 rounded">
          Draft
        </span>
      );
    default:
      return null;
  }
}

interface WaitlistEntry {
  _id: string;
  condoId: {
    _id: string;
    name: string;
    location: { borough?: string; neighborhood?: string };
    price?: number;
    bedrooms?: number;
    bathrooms?: number;
    sqft?: number;
    waitlistCount?: number;
    images?: string[];
  };
  joinedAt?: string;
}

const MyCondosPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const isPublishedTab = tab === 'published';
  const isPendingTab = tab === 'pending';
  const isInterestGroupTab = tab === 'interest-groups';
  const [condos, setCondos] = useState<Condo[]>([]);
  const [interestEntries, setInterestEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [withdrawingId, setWithdrawingId] = useState<string | null>(null);
  const [confirmWithdrawId, setConfirmWithdrawId] = useState<string | null>(null);
  const [confirmLeaveId, setConfirmLeaveId] = useState<string | null>(null);
  const [leavingId, setLeavingId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (isInterestGroupTab) {
        const result = await condoService.getWaitlistEntries();
        if (result.success && result.data) {
          setInterestEntries((result.data as any).entries ?? []);
        } else {
          setError('Failed to load your interest groups.');
        }
      } else {
        const result = await condoService.getMySubmissions();
        if (result.success && result.data) {
          setCondos(result.data.condos);
        } else {
          setError('Failed to load your condos.');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, [isInterestGroupTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleWithdraw = async (id: string) => {
    setWithdrawingId(id);
    try {
      const res = await condoService.withdrawSubmission(id);
      if (res.success) {
        await loadData();
      } else {
        setError((res as any).message || 'Failed to withdraw submission.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to withdraw submission.');
    } finally {
      setWithdrawingId(null);
      setConfirmWithdrawId(null);
    }
  };

  const handleLeave = async (condoId: string) => {
    setLeavingId(condoId);
    try {
      await condoService.leaveWaitlist(condoId);
      setInterestEntries(prev => prev.filter(e => e.condoId?._id !== condoId));
    } catch (err: any) {
      setError(err.message || 'Failed to leave interest group.');
    } finally {
      setLeavingId(null);
      setConfirmLeaveId(null);
    }
  };

  const pageTitle = isPublishedTab
    ? 'Published Condos'
    : isPendingTab
    ? 'Pending & Drafts'
    : isInterestGroupTab
    ? 'Interest Groups'
    : 'My Condos';

  const pageSubtitle = isPublishedTab
    ? 'Your approved listings visible to other users.'
    : isPendingTab
    ? 'Submissions under review, returned for revision, or saved as drafts.'
    : isInterestGroupTab
    ? "Condos you've joined an interest group for."
    : "Track the status of condos you've submitted.";

  const filteredCondos = condos.filter(c =>
    isPublishedTab ? c.status === 'approved'
    : isPendingTab ? c.status !== 'approved'
    : true
  );

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-cream border-2 border-coffee text-coffee px-3 py-2 rounded-lg hover:bg-white transition-colors shadow-sm text-sm font-medium"
            >
              ← Dashboard
            </button>
            <div>
              <h1 className="text-3xl font-bold text-coffee mb-1">{pageTitle}</h1>
              <p className="text-coffee opacity-80 text-sm">{pageSubtitle}</p>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center py-12 text-coffee">Loading…</div>
        )}

        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6 text-red-800">
            {error}
          </div>
        )}

        {/* Interest Groups tab */}
        {!loading && !error && isInterestGroupTab && (
          interestEntries.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-coffee text-xl mb-2">No interest groups yet</p>
              <p className="text-coffee opacity-80 mb-6">
                Join an interest group on any condo listing to track it here.
              </p>
              <button
                onClick={() => navigate('/condos')}
                className="bg-terracotta text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm"
              >
                Browse Condos
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {interestEntries.map((entry) => {
                const condo = entry.condoId;
                const condoId = condo?._id ?? '';
                const primaryImage = condo?.images && condo.images.length > 0 ? condo.images[0] : null;
                const locationText = condo?.location?.neighborhood
                  ? `${condo.location.neighborhood} · ${condo.location.borough}`
                  : condo?.location?.borough ?? '—';
                const unitDetail = [
                  condo?.bedrooms != null ? `${condo.bedrooms} bd` : null,
                  condo?.bathrooms != null ? `${condo.bathrooms} ba` : null,
                ].filter(Boolean).join(' · ');
                const joinedDate = entry.joinedAt
                  ? new Date(entry.joinedAt).toLocaleDateString()
                  : '';

                return (
                  <div
                    key={entry._id}
                    className="bg-cream border-2 border-coffee rounded-xl overflow-hidden shadow-sm"
                  >
                    {/* Image */}
                    <div className="h-40 bg-gray-100 overflow-hidden">
                      {primaryImage ? (
                        <img
                          src={primaryImage}
                          alt={condo?.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-coffee opacity-40 text-sm">
                          No Image
                        </div>
                      )}
                    </div>

                    <div className="p-4">
                      {/* Name + badge */}
                      <div className="flex justify-between items-start mb-1">
                        <h2 className="text-base font-bold text-coffee leading-tight flex-1 mr-2">
                          {condo?.name}
                        </h2>
                        <span className="inline-block bg-steel/20 border border-steel text-steel text-xs font-semibold px-2 py-0.5 rounded whitespace-nowrap">
                          In Group
                        </span>
                      </div>

                      {/* Location */}
                      <p className="text-coffee opacity-70 text-sm mb-1">{locationText}</p>

                      {/* Price */}
                      {condo?.price != null && (
                        <p className="text-coffee text-sm font-medium mb-1">
                          ${condo.price.toLocaleString()} / mo
                        </p>
                      )}

                      {/* Unit details */}
                      {unitDetail && (
                        <p className="text-coffee opacity-60 text-xs mb-1">{unitDetail}</p>
                      )}

                      {/* Member count + joined date */}
                      <p className="text-coffee opacity-50 text-xs mb-4">
                        {condo?.waitlistCount != null ? `${condo.waitlistCount} members` : ''}
                        {condo?.waitlistCount != null && joinedDate ? ' · ' : ''}
                        {joinedDate ? `Joined ${joinedDate}` : ''}
                      </p>

                      {/* Buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/condos/${condoId}`)}
                          className="flex-1 bg-white border-2 border-coffee text-coffee text-sm font-medium px-3 py-2 rounded-lg hover:bg-cream transition-colors"
                        >
                          View
                        </button>
                        {confirmLeaveId === condoId ? (
                          <>
                            <button
                              onClick={() => handleLeave(condoId)}
                              disabled={leavingId === condoId}
                              className="flex-1 bg-red-50 border-2 border-red-400 text-red-800 text-sm font-medium px-3 py-2 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                            >
                              {leavingId === condoId ? 'Leaving...' : 'Confirm Leave'}
                            </button>
                            <button
                              onClick={() => setConfirmLeaveId(null)}
                              className="bg-white border-2 border-coffee text-coffee text-sm px-3 py-2 rounded-lg hover:bg-cream transition-colors"
                            >
                              Keep
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setConfirmLeaveId(condoId)}
                            className="flex-1 bg-white border-2 border-red-300 text-red-700 text-sm font-medium px-3 py-2 rounded-lg hover:bg-red-50 transition-colors"
                          >
                            Leave Group
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {/* Published / Pending tabs */}
        {!loading && !error && !isInterestGroupTab && filteredCondos.length === 0 && (
          <div className="text-center py-12">
            <p className="text-coffee text-xl mb-2">
              {isPublishedTab ? 'No published condos yet' : isPendingTab ? 'Nothing in progress' : 'No condos submitted yet'}
            </p>
            <p className="text-coffee opacity-80 mb-6">
              {isPublishedTab
                ? 'Submit a condo and get it approved to see it here.'
                : isPendingTab
                ? 'Start a new submission or save a draft to continue later.'
                : 'Know of a building looking for tenants? List it here for review.'}
            </p>
            <button
              onClick={() => navigate('/condos/create')}
              className="bg-terracotta text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm"
            >
              Submit a Condo
            </button>
          </div>
        )}

        {!loading && !error && !isInterestGroupTab && filteredCondos.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCondos.map((condo) => {
              const condoId = condo.id ?? condo._id ?? '';
              const status = condo.status as CondoStatus | undefined;

              if (status === 'approved') {
                const primaryImage = condo.images && condo.images.length > 0 ? condo.images[0] : null;
                return (
                  <div
                    key={condoId}
                    className="bg-cream border-2 border-coffee rounded-xl overflow-hidden shadow-sm"
                  >
                    {/* Image */}
                    <div className="h-40 bg-gray-100 overflow-hidden">
                      {primaryImage ? (
                        <img
                          src={primaryImage}
                          alt={condo.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-coffee opacity-40 text-sm">
                          No Image
                        </div>
                      )}
                    </div>

                    <div className="p-4">
                      {/* Name + badge */}
                      <div className="flex justify-between items-start mb-1">
                        <h2 className="text-base font-bold text-coffee leading-tight flex-1 mr-2">
                          {condo.name}
                        </h2>
                        {getStatusBadge(status)}
                      </div>

                      {/* Location */}
                      <p className="text-coffee opacity-70 text-sm mb-1">
                        {condo.location.neighborhood
                          ? `${condo.location.neighborhood} · ${condo.location.borough}`
                          : condo.location.borough}
                      </p>

                      {/* Price */}
                      {condo.price != null && (
                        <p className="text-coffee text-sm font-medium mb-1">
                          ${condo.price.toLocaleString()} / mo
                        </p>
                      )}

                      {/* Unit details */}
                      <p className="text-coffee opacity-60 text-xs mb-4">
                        {[
                          condo.bedrooms != null ? `${condo.bedrooms} bd` : null,
                          condo.bathrooms != null ? `${condo.bathrooms} ba` : null,
                          condo.sqft != null ? `${condo.sqft.toLocaleString()} sqft` : null,
                        ].filter(Boolean).join(' · ')}
                      </p>

                      {/* Edit button */}
                      <button
                        onClick={() => navigate(`/condos/edit/${condoId}`)}
                        className="w-full bg-white border-2 border-coffee text-coffee text-sm font-medium px-3 py-2 rounded-lg hover:bg-cream transition-colors"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={condoId}
                  className="bg-cream border-2 border-coffee rounded-xl p-5 shadow-sm"
                >
                  {/* Status badge */}
                  <div className="flex justify-between items-start mb-3">
                    <h2 className="text-lg font-bold text-coffee leading-tight flex-1 mr-2">
                      {condo.name}
                    </h2>
                    {getStatusBadge(status, condo.rejectionReason)}
                  </div>

                  <p className="text-coffee opacity-70 text-sm mb-1">
                    {condo.location.borough}
                    {condo.location.neighborhood ? ` · ${condo.location.neighborhood}` : ''}
                  </p>

                  {condo.price != null && (
                    <p className="text-coffee text-sm mb-3">
                      ${condo.price.toLocaleString()} / mo
                    </p>
                  )}

                  {/* Decline reason */}
                  {status === 'declined' && condo.rejectionReason && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                      <p className="text-red-800 text-xs font-semibold mb-1">Reason:</p>
                      <p className="text-red-700 text-sm">{condo.rejectionReason}</p>
                    </div>
                  )}

                  <p className="text-coffee opacity-50 text-xs mt-3">
                    Submitted {condo.createdAt ? new Date(condo.createdAt).toLocaleDateString() : ''}
                  </p>

                  {(status === 'draft' || status === 'pending' || status === 'declined') && (
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => navigate(`/condos/edit/${condoId}`)}
                        className="flex-1 bg-white border-2 border-coffee text-coffee text-sm font-medium px-3 py-2 rounded-lg hover:bg-cream transition-colors"
                      >
                        Edit
                      </button>
                      {status === 'pending' && (
                        confirmWithdrawId === condoId ? (
                          <div className="flex gap-2 flex-1">
                            <button
                              onClick={() => handleWithdraw(condoId)}
                              disabled={withdrawingId === condoId}
                              className="flex-1 bg-red-50 border-2 border-red-400 text-red-800 text-sm font-medium px-3 py-2 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                            >
                              {withdrawingId === condoId ? 'Cancelling...' : 'Confirm Cancel'}
                            </button>
                            <button
                              onClick={() => setConfirmWithdrawId(null)}
                              className="bg-white border-2 border-coffee text-coffee text-sm px-3 py-2 rounded-lg hover:bg-cream transition-colors"
                            >
                              Keep
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmWithdrawId(condoId)}
                            className="flex-1 bg-white border-2 border-red-300 text-red-700 text-sm font-medium px-3 py-2 rounded-lg hover:bg-red-50 transition-colors"
                          >
                            Cancel Submission
                          </button>
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCondosPage;
